import type { QueryClient } from '@tanstack/react-query'

/**
 * Prefetch data for the user's home page during the login animation.
 * Uses queryClient.prefetchQuery so data is warm in the React Query cache
 * by the time the page mounts — no loading spinners on landing.
 *
 * Failures are silently ignored (the page will just fetch normally).
 */
export function prefetchHomeData(queryClient: QueryClient, homeRoute: string) {
  switch (homeRoute) {
    case '/workspace/queue':
      return prefetchWorkspace(queryClient)
    case '/system-admin/users':
      return prefetchSystemAdmin(queryClient)
    case '/client-admin/overview':
    case '/client-admin/pipeline':
      return prefetchClientAdmin(queryClient)
    case '/engine-admin/overview':
      return prefetchEngineAdmin(queryClient)
    default:
      return prefetchWorkspace(queryClient)
  }
}

async function prefetchWorkspace(qc: QueryClient) {
  const { workspaceApi } = await import('@/features/workspace/api/workspace.api')
  const defaultParams = { limit: 25, sort_by: 'priority', sort_order: 'asc', view_mode: 'my' }
  qc.prefetchQuery({
    queryKey: ['workspace', 'queue', defaultParams],
    queryFn: () => workspaceApi.getQueue(defaultParams),
    staleTime: 30_000,
  })
}

async function prefetchSystemAdmin(qc: QueryClient) {
  const { systemAdminApi } = await import('@/features/system-admin/api/system-admin.api')
  // Fire all three in parallel
  qc.prefetchQuery({
    queryKey: ['system-admin', 'users', { limit: 25, offset: 0 }],
    queryFn: () => systemAdminApi.listUsers({ limit: 25, offset: 0 }),
  })
  qc.prefetchQuery({
    queryKey: ['system-admin', 'roles'],
    queryFn: () => systemAdminApi.listRoles(),
  })
  qc.prefetchQuery({
    queryKey: ['system-admin', 'clients'],
    queryFn: () => systemAdminApi.listClients(),
  })
}

async function prefetchClientAdmin(qc: QueryClient) {
  const { clientAdminApi } = await import('@/features/client-admin/api/client-admin.api')
  qc.prefetchQuery({
    queryKey: ['client-admin', 'overview', 'today'],
    queryFn: () => clientAdminApi.getOverview('today'),
    staleTime: 30_000,
  })
}

async function prefetchEngineAdmin(qc: QueryClient) {
  const { engineAdminApi } = await import('@/features/engine-admin/api/engine-admin.api')
  qc.prefetchQuery({
    queryKey: ['engine-admin', 'overview', '7d'],
    queryFn: () => engineAdminApi.getOverview('7d'),
    staleTime: 30_000,
  })
}
